import React, { useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getMachine } from '../../actions/machine';
import { ArrowLeft, Edit, Trash2, Settings } from 'react-feather';
import { Button, Card, Row, Col, Badge } from 'react-bootstrap';
import { usePermission } from '../../hooks/usePermission';
import { setUpdateMachineId, setDeleteMachineId } from '../../actions/machine';
import Spinner from '../layout/Spinner';
import MachineUpdateForm from './MachineUpdateForm';
import MachineDeleteForm from './MachineDeleteForm';

const MachinePage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { userCan } = usePermission();
    const { machine, loading } = useSelector(state => state.machine);

    useEffect(() => {
        if (id) {
            dispatch(getMachine(id));
        }
    }, [dispatch, id]);

    if (!userCan('read machines')) {
        return <div>Sizda ushbu uskunani ko'rish huquqi yo'q.</div>;
    }

    if (loading) {
        return <Spinner />;
    }

    if (!machine) {
        return <div>Uskuna topilmadi.</div>;
    }

    const getStatusBadge = (status) => {
        const statusConfig = {
            'active': { variant: 'success', text: 'Faol' },
            'maintenance': { variant: 'warning', text: 'Ta\'mirlash' },
            'inactive': { variant: 'secondary', text: 'Nofaol' }
        };
        
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const handleEdit = () => {
        dispatch(setUpdateMachineId(machine.id));
    };

    const handleDelete = () => {
        dispatch(setDeleteMachineId(machine.id));
    };

    return (
        <Fragment>
            <div className="container-fluid p-0">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <Link to="/machines" className="btn btn-outline-secondary me-3">
                                    <ArrowLeft size={18} />
                                </Link>
                                <div>
                                    <h1 className="h3 mb-0">{machine.name}</h1>
                                    <p className="text-muted mb-0">Uskuna tafsilotlari</p>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {userCan('edit machines') && (
                                    <Button variant="outline-primary" onClick={handleEdit}>
                                        <Edit size={18} className="me-1" />
                                        Tahrirlash
                                    </Button>
                                )}
                                {userCan('delete machines') && (
                                    <Button variant="outline-danger" onClick={handleDelete}>
                                        <Trash2 size={18} className="me-1" />
                                        O'chirish
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Machine Details */}
                <Row>
                    <Col lg={8}>
                        <Card>
                            <Card.Header>
                                <Card.Title className="mb-0">
                                    <Settings size={18} className="me-2" />
                                    Asosiy ma'lumotlar
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Nomi:</label>
                                            <p className="mb-0">{machine.name}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Model:</label>
                                            <p className="mb-0">{machine.model || 'Belgilanmagan'}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Seriya raqami:</label>
                                            <p className="mb-0">{machine.serial_number || 'Belgilanmagan'}</p>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Holati:</label>
                                            <div>{getStatusBadge(machine.status)}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Firma:</label>
                                            <p className="mb-0">{machine.firm?.name || 'Belgilanmagan'}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Operator:</label>
                                            <p className="mb-0">
                                                {machine.operator 
                                                    ? `${machine.operator.first_name || ''} ${machine.operator.last_name || ''}`.trim()
                                                    : 'Belgilanmagan'
                                                }
                                            </p>
                                        </div>
                                    </Col>
                                </Row>
                                {machine.description && (
                                    <div className="mt-3">
                                        <label className="form-label fw-bold">Tavsif:</label>
                                        <p className="mb-0">{machine.description}</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card>
                            <Card.Header>
                                <Card.Title className="mb-0">Qo'shimcha ma'lumotlar</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">ID:</label>
                                    <p className="mb-0">#{machine.id}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Yaratilgan sana:</label>
                                    <p className="mb-0">
                                        {machine.created_at 
                                            ? new Date(machine.created_at).toLocaleDateString('uz-UZ')
                                            : 'Noma\'lum'
                                        }
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Oxirgi yangilanish:</label>
                                    <p className="mb-0">
                                        {machine.updated_at 
                                            ? new Date(machine.updated_at).toLocaleDateString('uz-UZ')
                                            : 'Noma\'lum'
                                        }
                                    </p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {userCan('edit machines') && (
                <MachineUpdateForm/>
            )}

            {userCan('delete machines') && (
                <MachineDeleteForm/>
            )}
        </Fragment>
    );
};

export default MachinePage; 